import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Stack,
    TextField,
    Checkbox,
    IconButton,
    InputAdornment,
    Box,
    CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectAvatar } from '../ProjectAvatar/ProjectAvatar';
import { userProjectsSearchSelector } from '../../selectors/selectors';
import { IProject, ISound } from '../../types/global/helper';
import { searchUserProjects } from '../../redux/project/actions';
import _ from 'lodash';
import CustomButton from '../Button/CustomButton';
import { isEmpty } from '../../utils/lodash';
import { Close, Clear, Add } from '@mui/icons-material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { SoundAvatar } from '../SoundAvatar/SoundAvatar';
import { EmptySearchBlock } from '../EsSidebar/EsSearch/EmptySearchBlock';

export type TMediaType = 'audio' | 'video' | 'picture';

interface ProjectSearchDialogProps {
    open: boolean;
    onClose: () => void;
    type?: TMediaType;
    maxSelection?: number;
    onSubmit?: (payload: any) => void;
    onAddClick?: () => void;
    enableAdd?: boolean;
}

const MAX_SELECTION = 5;

const UserProjectSearchDialogue: React.FC<ProjectSearchDialogProps> = ({
    open,
    onClose,
    type = 'video',
    maxSelection = MAX_SELECTION,
    onSubmit,
    onAddClick,
    enableAdd,
}) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const { projects, loading, sounds } = useSelector(userProjectsSearchSelector);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedProjects, setSelectedProjects] = useState<any>([]);
    const [localLoading, setLocalLoading] = useState<boolean>(false);

    // Use loading from selector and local loading state to determine UI state
    const isSearching = loading || localLoading;

    useEffect(() => {
        if (open && type) {
            setLocalLoading(true);
            dispatch(searchUserProjects({ type, page: 1 }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, open, type]);

    // Reset local loading when redux loading state changes
    useEffect(() => {
        if (!loading) {
            setLocalLoading(false);
        }
    }, [loading]);

    const handleSelection = (doc: any) => {
        const isAlreadySelected = selectedProjects.find((proj: any) => proj._id === doc._id);
        if (isAlreadySelected) {
            const selected = selectedProjects.filter((proj: any) => proj._id !== doc._id);
            setSelectedProjects(selected as any);
        } else if (selectedProjects.length < maxSelection) {
            setSelectedProjects([...selectedProjects, doc as any]);
        }
    };

    const handleDebouncedSearch = useRef(
        _.debounce((searchTerm: string) => {
            setLocalLoading(true);
            if (searchTerm.length > 0) {
                dispatch(searchUserProjects({ searchTerm, type, page: 1 }));
            } else {
                dispatch(searchUserProjects({ type, page: 1 }));
            }
        }, 500),
    ).current;

    const handleSearchInputChange = (event: { target: { value: string } }) => {
        const newValue = event.target.value;
        setSearchValue(newValue);
        handleDebouncedSearch(newValue);
    };

    const handleClearSearch = () => {
        setSearchValue('');
        setLocalLoading(true);
        dispatch(searchUserProjects({ type, page: 1 }));
        handleDebouncedSearch.cancel();
    };

    useEffect(() => {
        return () => {
            handleDebouncedSearch.cancel();
        };
    }, [handleDebouncedSearch]);

    const handleSubmit = () => {
        onSubmit && onSubmit(selectedProjects);
        setSelectedProjects([]);
        onClose();
    };

    const renderResults = () => {
        let content;

        if (type !== 'audio') {
            if (isEmpty(projects)) {
                content = (
                    <EmptySearchBlock subTitle="Uploaded content will appear here. Click the upload button to create new content" />
                );
            } else {
                content = projects.map((project: any) => {
                    const isAlreadySelected = selectedProjects.find((proj: any) => proj._id === project._id);
                    return (
                        <Stack
                            key={project._id}
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                            width={'100%'}
                            mb={1}>
                            <Stack
                                flexDirection="row"
                                width={'100%'}
                                alignItems="center"
                                justifyContent="space-between">
                                <ProjectAvatar project={project as IProject} />
                                <Checkbox
                                    checked={!!isAlreadySelected}
                                    onChange={() => handleSelection(project)}
                                    disabled={selectedProjects.length >= maxSelection && !isAlreadySelected}
                                    sx={{
                                        color: theme[themeStyle].gray_sidebar,
                                        '&.Mui-checked': {
                                            color: theme[themeStyle].highlight,
                                        },
                                        '&.MuiCheckbox-indeterminate': {
                                            color: theme[themeStyle].highlight,
                                        },
                                    }}
                                />
                            </Stack>
                        </Stack>
                    );
                });
            }
        } else {
            if (isEmpty(sounds)) {
                content = (
                    <EmptySearchBlock subTitle="Uploaded content will appear here. Click the upload button to create new content" />
                );
            } else {
                content = sounds.map((sound: any) => {
                    const isAlreadySelected = selectedProjects.find((proj: any) => proj._id === sound._id);
                    return (
                        <Stack
                            key={sound._id}
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                            width={'100%'}
                            mb={1}>
                            <Stack
                                flexDirection="row"
                                width={'100%'}
                                alignItems="center"
                                justifyContent="space-between">
                                <SoundAvatar sound={sound as ISound} />
                                <Checkbox
                                    checked={!!isAlreadySelected}
                                    onChange={() => handleSelection(sound)}
                                    disabled={selectedProjects.length >= maxSelection && !isAlreadySelected}
                                    sx={{
                                        color: theme[themeStyle].gray_sidebar,
                                        '&.Mui-checked': {
                                            color: theme[themeStyle].highlight,
                                        },
                                        '&.MuiCheckbox-indeterminate': {
                                            color: theme[themeStyle].highlight,
                                        },
                                    }}
                                />
                            </Stack>
                        </Stack>
                    );
                });
            }
        }

        return (
            <Box sx={{ position: 'relative', minHeight: '200px' }}>
                {isSearching && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 2,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            borderRadius: 1,
                        }}>
                        <CircularProgress size={40} sx={{ color: theme[themeStyle].true_white }} />
                    </Box>
                )}
                <Box
                    sx={{
                        opacity: isSearching ? 0.4 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                    }}>
                    {content}
                </Box>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    width: '90%',
                    height: '100%',
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                },
            }}>
            <DialogContent>
                <>
                    <Stack direction="row" alignItems="center" gap={2} mb={2}>
                        <TextField
                            variant="outlined"
                            placeholder={`Search your ${type}...`}
                            value={searchValue}
                            onChange={handleSearchInputChange}
                            fullWidth
                            InputProps={{
                                endAdornment: searchValue ? (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleClearSearch} edge="end">
                                            <Clear />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                style: {
                                    color: theme[themeStyle].tag_white,
                                    border: '0.5px solid white',
                                },
                            }}
                        />
                        {enableAdd && (
                            <IconButton
                                onClick={onAddClick}
                                sx={{
                                    cursor: 'pointer',
                                }}>
                                <Add
                                    sx={{
                                        color: theme[themeStyle].tag_white,
                                    }}
                                />
                            </IconButton>
                        )}
                        <IconButton
                            onClick={onClose}
                            sx={{
                                cursor: 'pointer',
                            }}>
                            <Close
                                sx={{
                                    color: theme[themeStyle].tag_white,
                                }}
                            />
                        </IconButton>
                    </Stack>
                    <Stack ref={scrollRef}>{renderResults()}</Stack>
                </>
            </DialogContent>
            <DialogActions>
                <CustomButton
                    label="Submit"
                    onClick={handleSubmit}
                    disabled={isEmpty(selectedProjects)}
                    sx={{ width: '100%' }}
                />
            </DialogActions>
        </Dialog>
    );
};

export { UserProjectSearchDialogue };
